/**
 * @file pricePer.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description Pricing & Costing: a cost or sale price typed per tonne, per kg
 *              or per the line's own unit (Cam, 28 Oct 2026 — "Allow cost/ sale
 *              price figures to be applied per tonne or per other unit from
 *              costing/ pricing (user can change there) so they can save doing
 *              maths they don't need to"), and a group whose lines are in
 *              different units made clear.
 *
 *   What is STORED is always per the line's own unit (a bag's cost on a bag
 *   line) — the figure typed is turned into it here: per tonne × the line's
 *   kg ÷ 1000, per kg × the line's kg. Never converted: milk powder (its
 *   price is per bag, utils/itemCostUnit), a None line (no weight), a line
 *   whose unit nobody knows — those take a figure per their own unit only,
 *   and a group's per-tonne figure leaves them out, named.
 *
 *   Which unit the box starts on (tracker q03, Cam: "Per tonne"): a group
 *   whose lines are in different units starts per tonne, one in a single unit
 *   in that unit, a line on its own in its unit; the person can switch, and
 *   the screen remembers their last choice for each (usePersistedState).
 *
 *   A choice typed this way is { id: 'manual', value: <per the line's unit>,
 *   entered: { value: <as typed>, per } } — `value` is what confirm writes,
 *   through the existing writers; `entered` is only what the box shows.
 *
 *   1.1.0 (Cam, 28 Oct 2026 — D7 note: "so long as the user is totally clear
 *   what £/ unit UNITS actually are (i.e. whether £100/T or /Tote or /25kg Bag
 *   etc.)"): perUnitLabel — what every cost and price figure on a line is
 *   per, always said: "/T", "/25kg bag", "/500kg tote", "/bag" for milk
 *   powder of no known weight, "/each" for a None line, "/unit?" (flagged)
 *   when nobody knows the line's unit.
 *
 *   1.2.0 (2026-10-28, N6): perUnitLabel is utils/unitPriceLabel's — the ONE
 *   rule every screen uses — re-exported here so the screen's imports stand.
 *   Its words are that rule's: " each" for None, " (unit not set)" for an
 *   unknown unit (was "/each", "/unit?"); a bulk product with no weight reads
 *   per tonne. perOptions and normalisedText name a line's unit by it too.
 */

import { sideChoices, sideFigure } from './pricingCostingView';
import { money } from './pricingCosting';
import { perUnitLabel } from './unitPriceLabel';

/** What a line's figures are per — utils/unitPriceLabel's one rule (1.2.0). */
export { perUnitLabel };

export const PER_UNIT = 'unit';
export const PER_TONNE = 't';
export const PER_KG = 'kg';

const blank = (v) => v === '' || v === null || v === undefined || String(v).trim() === '';
const num = (v) => (blank(v) ? null : (Number.isFinite(Number(v)) ? Number(v) : null));
const round4 = (n) => Math.round((Number(n) + Number.EPSILON) * 10000) / 10000;
/** A figure as typed: no separators, no trailing zeros past 2dp. */
export const plainFigure = (x) => (x === null || x === undefined || !Number.isFinite(Number(x))
  ? '' : round4(x).toFixed(4).replace(/0{1,2}$/, ''));

/** The line's unit, kg (0 = None), or null when nobody knows. */
export const lineKgOf = (v) => num(v?.unit_kg);
const isMilk = (v) => String(v?.item?.range || '').trim().toUpperCase() === '200P';

/** Why this line cannot take a figure per `per` (null = it can). */
export function perBlock(v, per) {
  if (!per || per === PER_UNIT) return null;
  if (isMilk(v)) return 'Milk powder is priced per bag — type it per bag';
  const kg = lineKgOf(v);
  if (kg === null) return 'No known unit — set the line\'s unit first';
  if (kg === 0) return 'None — no weight, so no price per tonne';
  return null;
}

/** A figure typed per `per`, as the line's own unit's figure. */
export function toLineUnit(value, per, kg) {
  const x = num(value);
  if (x === null) return null;
  if (!per || per === PER_UNIT) return round4(x);
  const k = num(kg);
  if (k === null || k <= 0) return null;
  return round4(per === PER_TONNE ? (x * k) / 1000 : x * k);
}

/** A line-unit figure, as it reads per `per`. */
export function fromLineUnit(value, per, kg) {
  const x = num(value);
  if (x === null) return null;
  if (!per || per === PER_UNIT) return x;
  const k = num(kg);
  if (k === null || k <= 0) return null;
  return round4(per === PER_TONNE ? (x * 1000) / k : x / k);
}

/** The lines' known units, distinct (0 counts — None is a unit). */
const knownKgs = (rows) => [...new Set((rows || []).map(lineKgOf).filter(k => k !== null))];

/** Are these lines in more than one unit (or some in an unknown one)? */
export function unitsMixed(rows) {
  const list = rows || [];
  const known = knownKgs(list);
  return known.length > 1 || (known.length >= 1 && list.some(v => lineKgOf(v) === null));
}

/** The units a figure can be typed in for these lines: [{ value, label }]. */
export function perOptions(rows) {
  const list = (rows || []).filter(Boolean);
  const known = knownKgs(list).filter(k => k > 0);
  const convertible = list.some(v => !perBlock(v, PER_TONNE));
  const single = list.length === 1 || (!unitsMixed(list) && known.length <= 1);
  // The line's own unit in the one rule's words: "per T", "per 25kg bag", "each".
  const own = single && list[0] ? perUnitLabel(list[0]) : null;
  const unitLabel = own && own.known ? (own.kind === 'none' ? 'each' : `per ${unitWord(list[0])}`) : 'per line\'s own unit';
  const out = [{ value: PER_UNIT, label: unitLabel }];
  if (!convertible) return out;
  // A tonne line's own unit IS a tonne: no second "per T".
  if (!(single && known.length === 1 && known[0] === 1000)) out.push({ value: PER_TONNE, label: 'per T' });
  out.push({ value: PER_KG, label: 'per kg' });
  return out;
}

/**
 * Which unit the box starts on: the person's last choice for this kind of box,
 * else per tonne for lines in different units (q03), else their own unit.
 * @param {object[]} rows
 * @param {{ mixed?, single? }} remembered
 */
export function defaultPer(rows, remembered = {}) {
  const opts = perOptions(rows).map(o => o.value);
  const want = unitsMixed(rows) ? (remembered.mixed || PER_TONNE) : (remembered.single || PER_UNIT);
  return opts.includes(want) ? want : PER_UNIT;
}

/**
 * sideChoices (utils/pricingCostingView) with a typed figure's unit: a manual
 * `{ id: 'manual', value, per }` is turned into each line's own unit; a line
 * that cannot take it is skipped with why. Anything else is sideChoices as is.
 */
export function sideChoicesPer(rows, side, choice) {
  const per = choice?.per || PER_UNIT;
  if (choice?.id !== 'manual') return sideChoices(rows, side, choice);
  const base = sideChoices(rows, side, { id: 'manual', value: choice.value ?? '' });
  if (per === PER_UNIT) {
    return { ...base, set: base.set.map(x => ({ ...x, choice: { ...x.choice, entered: { value: choice.value ?? '', per } } })) };
  }
  const byKey = new Map((rows || []).map(v => [v.key, v]));
  const set = [];
  const skipped = [...base.skipped];
  for (const x of base.set) {
    const v = byKey.get(x.key);
    const why = perBlock(v, per);
    if (why) { skipped.push({ key: x.key, why }); continue; }
    const lineValue = blank(choice.value) ? '' : plainFigure(toLineUnit(choice.value, per, lineKgOf(v)));
    set.push({ key: x.key, choice: { id: 'manual', value: lineValue, entered: { value: choice.value ?? '', per } } });
  }
  return { set, skipped };
}

/**
 * The figure typed for these lines on one side, as typed: every line's
 * choice typed once with the same figure and unit → { value, per }, else null.
 * @param {string[]} keys
 * @param {Object<string, object>} choices  the screen's `${key}|${side}` → choice
 */
export function enteredOf(keys, side, choices) {
  let seen = null;
  for (const k of keys || []) {
    const c = choices?.[`${k}|${side}`];
    if (!c || c.id !== 'manual' || !c.entered) return null;
    const e = { value: String(c.entered.value ?? ''), per: c.entered.per || PER_UNIT };
    if (seen && (seen.value !== e.value || seen.per !== e.per)) return null;
    seen = e;
  }
  return seen;
}

/** "the line's unit" in words for a row: "25kg bag", "T", "bag", "each" (None) — the one rule. */
function unitWord(v) {
  const u = perUnitLabel(v);
  return u.known ? u.text.trim().replace(/^\//, '') : 'unit';
}
/** Lines "in T", "in 25kg bags", "in 500kg totes", "in 15.873kg packs". */
const unitPluralWord = (w) => (w === 'T' ? 'T' : (/(bag|tote|pack)$/.test(w) ? `${w}s` : `${w} packs`));
/** £ to the penny — a per-tonne figure worked out, not typed. */
const pounds = (x) => money(Math.round((Number(x) + Number.EPSILON) * 100) / 100);

/**
 * A group whose lines are in different units, or carry different figures,
 * normalised per tonne beside each line's own — so nobody types a bag's price
 * over a tonne line. '' when there is nothing to say (one unit, one figure,
 * or every line in tonnes).
 *   "£400.00/T — 2 lines in 25kg bags at £10.00 /25kg bag, 1 line in T at
 *    £400.00 /T; 1 line with no known unit (SO-D10123)"
 */
export function normalisedText(rows, side, labelOf = (v) => v?.key) {
  const list = (rows || []).filter(Boolean);
  if (list.length < 2) return '';
  const figs = list.map(v => ({ v, f: sideFigure(v, side), kg: lineKgOf(v) })).filter(x => x.f && x.f.value !== null && x.f.value > 0);
  if (!figs.length) return '';
  const mixed = unitsMixed(list);
  const values = new Set(figs.map(x => Math.round(x.f.value * 10000)));
  const anyPack = figs.some(x => x.kg !== null && x.kg > 0 && x.kg !== 1000);
  if (!mixed && !(values.size > 1 && anyPack)) return '';
  const perT = figs.filter(x => x.kg !== null && x.kg > 0 && !isMilk(x.v)).map(x => fromLineUnit(x.f.value, PER_TONNE, x.kg));
  const tLo = perT.length ? Math.min(...perT) : null;
  const tHi = perT.length ? Math.max(...perT) : null;
  const head = tLo === null ? '' : (tHi - tLo < 0.005 ? `${pounds(tLo)}/T` : `${pounds(tLo)}–${pounds(tHi)}/T`);
  const byUnit = new Map();
  for (const x of figs.filter(y => y.kg !== null)) {
    const w = unitWord(x.v);
    const e = byUnit.get(w) || { n: 0, vals: [] };
    e.n += 1; e.vals.push(x.f.value);
    byUnit.set(w, e);
  }
  const parts = [...byUnit.entries()].map(([w, e]) => {
    const lo = Math.min(...e.vals); const hi = Math.max(...e.vals);
    const at = hi - lo < 0.00005 ? money(lo) : `${money(lo)}–${money(hi)}`;
    const lines = `${e.n} line${e.n === 1 ? '' : 's'}`;
    return w === 'each' ? `${lines} with no weight at ${at} each` : `${lines} in ${unitPluralWord(w)} at ${at} /${w}`;
  });
  const unknown = list.filter(v => lineKgOf(v) === null);
  const tail = unknown.length ? `${unknown.length} line${unknown.length === 1 ? '' : 's'} with no known unit (${unknown.slice(0, 4).map(labelOf).join(', ')}${unknown.length > 4 ? '…' : ''})` : '';
  return [[head, parts.join(', ')].filter(Boolean).join(' — '), tail].filter(Boolean).join('; ');
}

/**
 * A group's total quantity when its lines count in different units: never one
 * bare figure adding bags to tonnes. "40 × 25kg bag + 0.75 T = 1.75 T"; the
 * tonnes only when every line's unit is known. null when the lines agree
 * (the caller shows its usual total).
 * @param {object[]} rows  rows with qty, unit ('T', '25kg bag', ''), unit_kg
 * @param {(r: { qty, unit }) => string} qtyText the screen's own quantity words
 */
export function groupQtyText(rows, qtyText) {
  const list = (rows || []).filter(Boolean);
  const units = new Map();
  for (const v of list) units.set(v.unit || '', (units.get(v.unit || '') || 0) + (num(v.qty) || 0));
  if (units.size <= 1) return null;
  const parts = [...units.entries()].map(([unit, qty]) => (unit ? qtyText({ qty, unit }) : `${qtyText({ qty, unit: '' })} (no unit)`));
  const allKnown = list.every(v => lineKgOf(v) !== null);
  const tonnes = allKnown ? list.reduce((s, v) => s + ((num(v.qty) || 0) * lineKgOf(v)) / 1000, 0) : null;
  return `${parts.join(' + ')}${tonnes !== null ? ` = ${qtyText({ qty: round4(tonnes), unit: 'T' })}` : ''}`;
}
